import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "./api";
import { useSession } from "./auth-client";
import { identifyOrganization, identifyUser } from "./posthog";

/**
 * Subscribes to the better-auth session and pipes user + active organization
 * identity into PostHog. Renders nothing — it's purely an effect carrier so
 * identity is tied to the provider tree's lifecycle without needing the rest
 * of the app to know about PostHog.
 *
 * Setting the `organization` group lets us target feature flags per-org from
 * the PostHog dashboard.
 */
export function PostHogIdentityBridge() {
  const { data } = useSession();
  const orgsQuery = useQuery({
    queryKey: ["orgs"],
    queryFn: api.orgs.list,
    enabled: !!data?.user,
  });

  const userId = data?.user?.id;
  const userEmail = data?.user?.email;
  const userName = data?.user?.name;
  useEffect(() => {
    identifyUser(userId ? { id: userId, email: userEmail, name: userName } : null);
  }, [userId, userEmail, userName]);

  const activeOrgId = data?.session?.activeOrganizationId ?? null;
  const activeOrg = orgsQuery.data?.find((o) => o.id === activeOrgId) ?? null;

  useEffect(() => {
    if (!data?.user) {
      identifyOrganization(null);
      return;
    }
    if (!activeOrgId) return;
    identifyOrganization({ id: activeOrgId, name: activeOrg?.name });
  }, [data?.user, activeOrgId, activeOrg?.name]);

  return null;
}
